import React, { createContext, useEffect, useState } from "react";
import walletsApi from "@api/wallets";
import destinationWalletsApi from "@api/destinationWallets";
import { __t } from "@core/translation/translation";
import { notification } from "@core/helper/notificationHelper";
import { useUserState } from "@features/User/context";
import { useRangerOptional } from "@context/rangerContext";
import withdrawApi from "@api/withdrawals";
import depositsApi from "@api/deposit";

let Context = createContext();

let initialState = {
  wallet: [],
  destinationWallets: [],
  destinationWalletsWithoutPagination: [],
  deposits: [],
  withdrawals: []
};

let Reducer = (state, action) => {
  switch (action.type) {
    case "SET_WALLET":
      return {
        ...state,
        wallet: action.payload
      };
    case "SET_DEST_WALLETS": {
      return {
        ...state,
        destinationWallets: action.payload
      };
    }
    case "ADD_WALLET": {
      const newWallets = [action.payload, ...state.destinationWallets];
      return {
        ...state,
        destinationWallets: newWallets,
        destinationWalletsWithoutPagination: newWallets
      };
    }
    case "SET_DEST_WALLETS_WITHOUT_PAPGINATION": {
      return {
        ...state,
        destinationWalletsWithoutPagination: action.payload
      };
    }
    case "DELETE_WALLET": {
      const newArrayAfterDelete = state?.destinationWallets?.filter(
        item => item?.id != action.payload?.id
      );
      return {
        ...state,
        destinationWallets: newArrayAfterDelete
      };
    }
    case "SET_ADDRESS":
      return {
        ...state,
        wallet: {
          ...state.wallet,
          [state.wallet[action?.payload?.index].address]: action.payload.address
        }
      };
    case "SET_WITHDRAWALS":
      return {
        ...state,
        withdrawals: action.payload
      };
    case "ADD_WITHDRAWAL":
      const newWithdrawals = [action.payload, ...state.withdrawals];
      return {
        ...state,
        withdrawals: newWithdrawals
      };
    case "SET_DEPOSITS":
      return {
        ...state,
        deposits: action.payload
      };
    case "ADD_DEPOSIT":
      const newDesposits = [action.payload, ...state.deposits];
      return {
        ...state,
        deposits: newDesposits
      };
    default:
      return state;
  }
};

function ContextProvider(props) {
  let [state, dispatch] = React.useReducer(Reducer, initialState);
  let value = { state, dispatch };
  const { authorized } = useUserState();
  const ranger = useRangerOptional();
  const [walletTick, setWalletTick] = useState(0);

  // WS4.5 — refresh wallets on private `balances` events
  useEffect(() => {
    if (!ranger?.onPrivateEvent) return undefined;
    return ranger.onPrivateEvent("balances", () =>
      setWalletTick((n) => n + 1)
    );
  }, [ranger?.onPrivateEvent]);

  useEffect(() => {
    if (!authorized) {
      return;
    }

    walletsApi
      .getMyWallets()
      .then(response => {
        dispatch({ type: "SET_WALLET", payload: response.content });
      })
      .catch(error =>
        notification(
          __t("Error"),
          "danger",
          error?.message
            ? error.message
            : __t("An error occurred on the server")
        )
      );
  }, [authorized, walletTick]);

  useEffect(() => {
    if (!authorized) {
      return;
    }

    destinationWalletsApi
        .getAllMyDestinationWallets({ params: { pageSize: 5, pageNumber: 0 } })
        .then(response => {
          dispatch({ type: "SET_DEST_WALLETS", payload: response.content });
        })
        .catch(error =>
          notification(
            __t("Error"),
            "danger",
            error?.message
              ? error.message
              : __t("An error occurred on the server")
          )
        );
  }, [authorized]);

  useEffect(() => {
    if (!authorized) {
      return;
    }

    destinationWalletsApi
        .getAllMyDestinationWalletsWithoutPagination()
        .then(response => {
          dispatch({
            type: "SET_DEST_WALLETS_WITHOUT_PAPGINATION",
            payload: response.content
          });
        })
        .catch(error =>
          notification(
            __t("Error"),
            "danger",
            error?.message
              ? error.message
              : __t("An error occurred on the server")
          )
        );
  }, [authorized]);

  useEffect(() => {
    if (!authorized) {
      return;
    }

    withdrawApi
        .getAllMyWithdrawals(3, 0)
        .then(response => {
          dispatch({ type: "SET_WITHDRAWALS", payload: response.content });
        })
        .catch(error =>
          notification(
            __t("Error"),
            "danger",
            error?.message
              ? error.message
              : __t("An error occurred on the server")
          )
        );
  }, [authorized]);

  useEffect(() => {
    if (!authorized) {
      return;
    }

    depositsApi
        .getAllDeposits(3, 0)
        .then(response => {
          dispatch({ type: "SET_DEPOSITS", payload: response.content });
        })
        .catch(error =>
          notification(
            __t("Error"),
            "danger",
            error?.message
              ? error.message
              : __t("An error occurred on the server")
          )
        );
  }, [authorized]);

  return <Context.Provider value={value}>{props.children}</Context.Provider>;
}

let ContextConsumer = Context.Consumer;

export { Context, ContextProvider, ContextConsumer };
